import { useRouter } from 'expo-router';
import { XStack, YStack } from 'tamagui';

import PSButton from '@/components/ui/PSButton';
import PSText from '@/components/ui/PSText';
import {
  PendingMembershipState,
  usePendingMembership,
} from '@/src/hooks/usePendingMembership';

/**
 * PendingPaymentBanner — the strip a user with an unconfirmed purchase sees
 * (molecule). On home above the first rail of events, and on profile in the slot
 * [[MembershipCard]] would occupy — the two places you'd go looking for a
 * membership that isn't there yet.
 *
 * ── It draws itself out ─────────────────────────────────────────────────
 * Nothing pending, nothing rendered. Unlike [[MembershipCard]], which is
 * non-nullable so its screen owns that decision, this one reads the session
 * itself: there is no heading to drop alongside it, and a hook can't be called
 * conditionally, so a screen that took a prop would have to grow the branch this
 * saves it.
 *
 * ── It owns its fetch and its destination ───────────────────────────────
 * `/me` reports a pending purchase as three keys with no plan on them, so the
 * name has to be fetched — see [[usePendingMembership]], which also explains why
 * the copy softens rather than disappears when it can't be. And there is exactly
 * one place to send someone: the membership's own page, where the payment can be
 * finished or called off. That doesn't vary by caller, so it isn't a prop (the
 * [[DownloadAppNotice]] precedent).
 */
export default function PendingPaymentBanner({
  pendingMembership,
  onCtaClick,
}: {
  pendingMembership: PendingMembershipState;
  onCtaClick: () => void;
}) {
  const router = useRouter();

  return (
    <YStack
      bg={'$backgroundSecondary'}
      rounded={'$base'}
      p={'$space.lg'}
      gap={'$space.md'}
      items="center"
    >
      <XStack items={'center'} gap={'$space.md'}>
        {/* Unnamed until the plan's name arrives, and possibly for good — see
          [[usePendingMembership]]. The pending payment is the fact; the name is
          the detail that makes it recognisable. */}
        <PSText.Body2 flex={1} color="$textDark">
          {pendingMembership.planName
            ? `Your payment for ${pendingMembership.planName} is pending`
            : 'Your membership payment is pending'}
        </PSText.Body2>
      </XStack>
      <PSButton
        title="Continue"
        onPress={onCtaClick}
        fullWidth
        variant="outlineBrand"
      />
    </YStack>
  );
}
